import { useEffect, useRef, useState } from 'react';
import { api, buildQuery } from '../../shared/api';

const runRequest = async ({
    query,
    queryVariables,
    variables,
    abortController,
}) => {

    const result = await api.executePostRequest(
        buildQuery(query, queryVariables),
        variables,
        abortController
    );

    if (result.errors?.length) {
        throw new Error(
            result.errors?.[0]?.error_message || 'Unknown network error occurred'
        );
    }

    if (!result?.data) {
        throw new Error(
            'Has no data attribute in network response'
        );
    }

    return { data: result.data };
};

export const useFetchData = <TData>({
    query,
    selectData,
    selectPageInfo,
    initialPage = 1,
    perPage = 15,
    optionalArray = []
}) => {

    const baseQueryVariablesRef = useRef<any>({});
    const baseVariablesRef = useRef<any>({});

    const [data, setData] = useState<TData | null>(null);

    const [page, setPage] = useState<number>(initialPage);
    const [totalRows, setTotalRows] = useState<number>(0);
    const [hasNextPage, setHasNextPage] = useState<boolean>(false);

    const [isLoading, setIsLoading] = useState<boolean | null>(null);
    const [error, setError] = useState<Error | null>(null);

    const abortController = useRef<AbortController | null>(null);

    useEffect(() => {
        return () => {
            if (abortController.current) {
                abortController.current.abort();
            }
        };
    }, []);


    const clearData = () => {

        if (abortController.current) {
            abortController.current.abort();
            abortController.current = null;
        }

        baseQueryVariablesRef.current = {};
        baseVariablesRef.current = {};

        setData(null);
        setPage(initialPage);
        setTotalRows(0);
        setHasNextPage(false);
        setIsLoading(null);
        setError(null);
    }

    useEffect(() => {
        if (optionalArray.length) clearData();
    }, optionalArray)

    const fetchData = async (currentPage: number) => {
        if (abortController.current) {
            abortController.current.abort();
        }
        const controller = new AbortController();
        abortController.current = controller;

        setIsLoading(true);
        setError(null);

        try {
            const variables = {
                ...baseVariablesRef.current,
                page: currentPage,
                page_size: perPage,
            };

            const result = await runRequest({
                query,
                queryVariables: baseQueryVariablesRef.current,
                variables,
                abortController: controller,
            });

            const pageInfo = selectPageInfo(result.data);

            setData(selectData(result.data));
            setPage(currentPage);
            setTotalRows(pageInfo.perPage * pageInfo.totalPages)
            setHasNextPage(pageInfo.hasNextPage);
        } catch (err: any) {
            if (err.name === 'AbortError') {
                console.log('Request aborted');
                return;
            }

            console.error(err.message);
            setError(err);
        } finally {
            if (abortController.current === controller) {
                setIsLoading(false);
            }
        }
    };

    const setBaseVariables = (queryVariables: any = {}, variables: any = {}) => {
        baseQueryVariablesRef.current = queryVariables;
        baseVariablesRef.current = variables;
        fetchData(1);
    };

    const loadMore = (requestedPage: number | null = null): void => {
        if (requestedPage) {
            fetchData(requestedPage);
        } else if (!isLoading && hasNextPage) {
            fetchData(page + 1);
        }
    };

    useEffect(() => {
        if (!isLoading && abortController.current) {
            abortController.current = null;
        }
    }, [isLoading]);

    const clearError = () => setError(null)

    return [
        setBaseVariables,
        {
            isLoading,
            data,
            page,
            totalRows,
            hasNextPage,
            error,
            loadMore,
            clearError
        }
    ] as const;
};
